import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  onSnapshot,
  orderBy,
  addDoc,
  getDoc,
  updateDoc,
  Timestamp,
  serverTimestamp,
  limit,
  deleteDoc
} from "firebase/firestore";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db, storage } from "@/config/firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { createNotification } from "./notificationService";

// Message type definition
interface Message {
  id: string;
  senderId: string;
  senderName: string;
  recipientId: string;
  content: string;
  timestamp: string;
  read: boolean;
  conversationId: string;
}

// Progress type definition
interface ProgressUpdate {
  id: string;
  userId: string;
  userName: string;
  courseId: string;
  courseName: string;
  completedItems: string[];
  totalItems: string[];
  progressPercentage: number;
  lastUpdated: string;
  instructorComments?: string;
  instructorId?: string;
  status: 'in_progress' | 'completed' | 'needs_improvement';
  totalHours?: number;
  hoursTaken?: number;
  instructorName?: string;
}

// Instructor payment type
interface InstructorPayment {
  id: string;
  instructorId: string;
  instructorName: string;
  studentName: string;
  totalAmount: number;
  amountPaid: number;
  amountRemaining: number;
  courseName: string;
  paymentStatus: 'unpaid' | 'partially_paid' | 'fully_paid';
  lastPaymentDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Gallery image type
interface GalleryImage {
  id: string;
  url: string;
  caption: string;
  createdAt: string;
}

/**
 * Login user with email and password
 * @param email User email
 * @param password User password
 * @returns The logged in user
 */
export const loginWithEmail = async (email: string, password: string) => {
  if (!auth) {
    console.error("Auth is not initialized");
    return null;
  }
  
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error("Error logging in:", error);
    throw error;
  }
};

/**
 * Get enrollments by user ID
 * @param userId User ID
 * @returns Array of enrollments
 */
export const getEnrollmentsByUserId = async (userId: string) => {
  if (!auth) {
    console.error("Auth is not initialized");
    return [];
  }
  
  try {
    const enrollmentsRef = collection(db, "enrollments");
    const q = query(enrollmentsRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    const enrollments: any[] = [];
    querySnapshot.forEach((doc) => {
      enrollments.push({ id: doc.id, ...doc.data() });
    });
    
    console.log(`Found ${enrollments.length} enrollments for user:`, userId);
    return enrollments;
  } catch (error) {
    console.error("Error getting enrollments by user ID:", error);
    throw error;
  }
};

/**
 * Subscribe to messages between two users
 */
export const subscribeToMessages = (
  senderId: string, 
  recipientId: string, 
  callback: (messages: Message[]) => void
) => {
  if (!db) {
    console.error("Firestore is not initialized");
    return () => {};
  }
  
  const messagesRef = collection(db, "messages");
  const q = query(
    messagesRef,
    where("conversationId", "==", [senderId, recipientId].sort().join("_")),
    orderBy("timestamp", "desc"),
    limit(50)
  );

  return onSnapshot(q, (snapshot) => {
    const messages: Message[] = [];
    snapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() } as Message);
    });
    callback(messages.reverse());
  });
};

/**
 * Get student progress
 * @param userId Student ID
 * @returns Array of progress updates
 */
export const getStudentProgress = async (userId: string): Promise<ProgressUpdate[]> => {
  if (!db) {
    console.error("Firestore is not initialized");
    return [];
  }
  
  try {
    const progressRef = collection(db, "progress");
    const q = query(
      progressRef,
      where("userId", "==", userId),
      orderBy("lastUpdated", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const progress: ProgressUpdate[] = [];
      
    querySnapshot.forEach((doc) => {
      progress.push({ id: doc.id, ...doc.data() } as ProgressUpdate);
    });
    
    return progress;
  } catch (error) {
    console.error("Error getting student progress:", error);
    throw error;
  }
};

/**
 * Subscribe to student progress updates
 */
export const subscribeToStudentProgress = (
  userId: string,
  callback: (progress: ProgressUpdate[]) => void
): (() => void) => {
  if (!db) {
    console.error("Firestore is not initialized");
    return () => {};
  }
  
  const progressRef = collection(db, "progress");
  const q = query(
    progressRef,
    where("userId", "==", userId),
    orderBy("lastUpdated", "desc")
  );
  
  return onSnapshot(q, (snapshot) => {
    const progress: ProgressUpdate[] = [];
    snapshot.forEach((doc) => {
      progress.push({ id: doc.id, ...doc.data() } as ProgressUpdate);
    });
    callback(progress);
  });
};

/**
 * Update student progress
 */
export const updateProgress = async (
  progressId: string,
  update: Partial<ProgressUpdate>
): Promise<void> => {
  if (!db) {
    console.error("Firestore is not initialized");
    return;
  }
  
  try {
    const progressRef = doc(db, "progress", progressId);
    await updateDoc(progressRef, {
      ...update,
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error("Error updating progress:", error);
    throw error;
  }
};

/**
 * Get all instructor payments
 * @returns Array of instructor payments
 */
export const getAllInstructorPayments = async (): Promise<InstructorPayment[]> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    const paymentsRef = collection(db, "instructorPayments");
    const q = query(paymentsRef, orderBy("instructorName", "asc"));
    const querySnapshot = await getDocs(q);
    
    const payments: InstructorPayment[] = [];
    querySnapshot.forEach((doc) => {
      payments.push({
        id: doc.id,
        ...doc.data()
      } as InstructorPayment);
    });
    
    return payments;
  } catch (error) {
    console.error("Error getting instructor payments:", error);
    throw error;
  }
};

/**
 * Upload an image to the gallery
 * @param file Image file to upload
 * @param caption Image caption
 * @returns Gallery image data
 */
export const uploadGalleryImage = async (file: File, caption: string): Promise<GalleryImage> => {
  if (!storage || !db) throw new Error("Firebase is not initialized");
  
  try {
    // Create a unique filename
    const filename = `gallery/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filename);
    
    // Upload the file
    await uploadBytes(storageRef, file);
    
    // Get the download URL
    const url = await getDownloadURL(storageRef);
    
    // Create the gallery document
    const galleryRef = collection(db, "gallery");
    const imageData: Omit<GalleryImage, 'id'> = {
      url,
      caption,
      createdAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(galleryRef, imageData);
    
    return {
      id: docRef.id,
      ...imageData
    };
  } catch (error) {
    console.error("Error uploading gallery image:", error);
    throw error;
  }
};

/**
 * Get all gallery images
 * @returns Array of gallery images
 */
export const getStudentGalleryImages = async (): Promise<GalleryImage[]> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    const galleryRef = collection(db, "gallery");
    const q = query(galleryRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    const images: GalleryImage[] = [];
    querySnapshot.forEach((doc) => {
      images.push({
        id: doc.id,
        ...doc.data()
      } as GalleryImage);
    });
    
    return images;
  } catch (error) {
    console.error("Error getting gallery images:", error);
    throw error;
  }
};

/**
 * Delete a gallery image
 * @param imageId Image ID to delete
 */
export const deleteGalleryImage = async (imageId: string): Promise<void> => {
  if (!storage || !db) throw new Error("Firebase is not initialized");
  
  try {
    // Get the image document first to get the storage URL
    const imageRef = doc(db, "gallery", imageId);
    const imageSnap = await getDoc(imageRef);
    
    if (!imageSnap.exists()) {
      throw new Error("Image not found");
    }
    
    const imageData = imageSnap.data() as GalleryImage;
    
    // Delete from storage
    const storageRef = ref(storage, imageData.url);
      await deleteObject(storageRef);
    
    // Delete from Firestore
    await deleteDoc(imageRef);
  } catch (error) {
    console.error("Error deleting gallery image:", error);
    throw error;
  }
};

/**
 * Process a payment to an instructor
 * @param paymentId Payment ID
 * @param amount Amount to process
 */
export const processInstructorPayment = async (paymentId: string, amount: number): Promise<void> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    // Get the current payment record
    const paymentRef = doc(db, "instructorPayments", paymentId);
    const paymentSnap = await getDoc(paymentRef);
    
    if (!paymentSnap.exists()) {
      throw new Error(`Payment with ID ${paymentId} not found`);
    }
    
    const payment = paymentSnap.data() as InstructorPayment;
    
    // Calculate new amounts
    const newAmountPaid = payment.amountPaid + amount;
    const newAmountRemaining = payment.totalAmount - newAmountPaid;
    const newPaymentStatus = newAmountRemaining <= 0 ? 'fully_paid' as const : 'partially_paid' as const;
    
    // Update the payment record
    await updateDoc(paymentRef, {
      amountPaid: newAmountPaid,
      amountRemaining: newAmountRemaining,
      paymentStatus: newPaymentStatus,
      lastPaymentDate: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    // Create a notification for the instructor
    createNotification(
      'instructor',
      'payment-processed',
      'Payment Processed',
      `A payment of £${amount.toFixed(2)} has been processed for your services.`
    );
    
  } catch (error) {
    console.error("Error processing instructor payment:", error);
    throw error;
  }
};
